import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { gamesRepositoryToken } from './repository';
import type { GameTypeDetail } from './types';
import { normalizeMasterCode } from './types';

interface Values {
  readonly code: string;
  readonly name: string;
  readonly reason: string;
}

export function GameTypeDialog({
  gameType,
  onClose,
  onSaved,
  open,
}: {
  readonly gameType?: GameTypeDetail | undefined;
  readonly onClose: () => void;
  readonly onSaved: (gameType: GameTypeDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(gamesRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<Values>({ defaultValues: defaults(gameType) });
  const normalizedCode = normalizeMasterCode(watch('code'));

  useEffect(() => {
    if (!open) return;
    reset(defaults(gameType));
    setError(null);
    setBusy(false);
    guard.current = false;
  }, [gameType, open, reset]);

  const submit = handleSubmit(async (values) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const response =
        gameType === undefined
          ? await repository.createGameType({
              code: normalizeMasterCode(values.code),
              name: values.name.trim(),
              reason: values.reason.trim(),
            })
          : await repository.updateGameType(gameType.id, {
              name: values.name.trim(),
              reason: values.reason.trim(),
            });
      onSaved(response.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('games.errors.mutation')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });

  return (
    <Dialog fullWidth maxWidth="sm" onClose={() => !guard.current && onClose()} open={open}>
      <DialogTitle>
        {t(gameType === undefined ? 'games.forms.gameTypeCreate' : 'games.forms.gameTypeEdit')}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="game-type-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            disabled={busy || gameType !== undefined}
            error={errors.code !== undefined}
            helperText={errors.code?.message}
            label={t('games.fields.gameTypeCode')}
            required
            {...register('code', {
              validate: (value) =>
                /^[A-Z0-9][A-Z0-9_-]{1,31}$/u.test(normalizeMasterCode(value)) ||
                t('games.validation.gameTypeCode'),
            })}
          />
          {gameType === undefined && normalizedCode.length > 0 ? (
            <Alert severity="info">
              <Typography variant="caption">{t('games.forms.normalizedCode')}</Typography>
              <Typography component="p" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {normalizedCode}
              </Typography>
            </Alert>
          ) : null}
          <TextField
            autoFocus={gameType !== undefined}
            disabled={busy}
            error={errors.name !== undefined}
            helperText={errors.name?.message}
            label={t('games.fields.gameTypeName')}
            required
            {...register('name', {
              maxLength: { value: 200, message: t('games.validation.nameMaximum') },
              validate: (value) => value.trim().length > 0 || t('games.validation.nameRequired'),
            })}
          />
          <TextField
            disabled={busy}
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('games.fields.reason')}
            minRows={2}
            multiline
            required
            {...register('reason', {
              maxLength: { value: 2000, message: t('games.validation.reasonMaximum') },
              validate: (value) => value.trim().length > 0 || t('games.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button form="game-type-form" loading={busy} type="submit" variant="contained">
          {t(gameType === undefined ? 'actions.create' : 'actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function defaults(gameType: GameTypeDetail | undefined): Values {
  return { code: gameType?.code ?? '', name: gameType?.name ?? '', reason: '' };
}
